/**
 * Project Context
 *
 * @deprecated This context is DEPRECATED. Use TenantContext instead.
 *
 * Migration guide:
 * - Replace `useProject()` with `useTenant()`
 * - Replace `currentProject` with `selectedProject`
 * - Replace `availableProjects` with `projects`
 * - Replace `setCurrentProject` with `setSelectedProject`
 *
 * TenantContext provides:
 * - Unified project selection for all users (platform + tenant)
 * - Cache invalidation when switching projects
 * - 30-second polling for permission changes
 * - Company selection for platform users
 *
 * This context is kept for backwards compatibility but will be removed in a future release.
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useEntitlements } from '@/hooks/useEntitlements';
import { apiClient } from '@/lib/api-client';
import type { Project } from '@/services/api/projectsService';
import { permissionsKeys } from '@/modules/users/hooks/permissionsQueryKeys';

/**
 * Response from /auth/users/me/accessible-projects/ endpoint
 */
interface AccessibleProjectsResponse {
  user_role: string;
  user_type: string;
  has_all_company_access: boolean;
  company_id: string | null;
  company_name: string | null;
  primary_project_id: string | null;
  projects: Project[];
}

interface ProjectContextType {
  currentProject: Project | null;
  availableProjects: Project[];
  isLoading: boolean;
  setCurrentProject: (project: Project | null) => void;
  hasMultipleProjects: boolean;
  shouldShowSelector: boolean;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

const STORAGE_KEY = 'owly_current_project_id';

interface ProjectProviderProps {
  children: ReactNode;
}

export function ProjectProvider({ children }: ProjectProviderProps) {
  const { user, isAuthenticated } = useAuth();
  const { isPlatform, isLoading: isLoadingEntitlements } = useEntitlements();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  // Only specific OWLY roles get unrestricted platform access
  const UNRESTRICTED_PLATFORM_ROLES = ['owly_admin', 'owly_support', 'owly_3d', 'owly_reviewer'];
  const isUnrestrictedPlatform = isPlatform && (
    user?.is_superuser ||
    user?.is_staff ||
    UNRESTRICTED_PLATFORM_ROLES.includes(user?.role || '')
  );

  // Platform users: fetch all projects via /projects/ endpoint
  const projectParams = (user?.is_superuser || !user?.company)
    ? { page_size: 100 }  // Superuser o sin company: ver todos
    : { company: user.company, page_size: 100 };  // Usuario regular: filtrar por company

  // Use useQuery directly with enabled condition for unrestricted platform users
  const { data: platformProjectsData, isLoading: platformProjectsLoading } = useQuery({
    queryKey: ['projects', projectParams],
    queryFn: () => apiClient.get<{ results: Project[] }>('/projects/', { params: projectParams }),
    enabled: isAuthenticated && !isLoadingEntitlements && isUnrestrictedPlatform,
    staleTime: 1 * 60 * 1000,
  });

  // Tenant users: fetch via accessible-projects endpoint for proper access control
  // This respects ProjectMembership and only returns projects user has access to
  const { data: accessibleProjectsData, isLoading: accessibleProjectsLoading } = useQuery({
    queryKey: ['project-context-accessible', user?.id],
    queryFn: async () => {
      const response = await apiClient.get<AccessibleProjectsResponse>('/auth/users/me/accessible-projects/');
      return response;
    },
    enabled: isAuthenticated && !isLoadingEntitlements && !isUnrestrictedPlatform,
    staleTime: 60 * 1000, // Data is fresh for 60 seconds
    refetchInterval: 30 * 1000, // Poll every 30 seconds for permission changes
    refetchIntervalInBackground: false, // Don't poll when tab is not focused
  });

  const [currentProject, setCurrentProjectState] = useState<Project | null>(null);

  // Use accessible projects for restricted users, all projects for unrestricted platform
  const projectsLoading = isUnrestrictedPlatform ? platformProjectsLoading : accessibleProjectsLoading;
  const availableProjects = isUnrestrictedPlatform
    ? (platformProjectsData?.results || [])
    : (accessibleProjectsData?.projects || []);
  const hasMultipleProjects = availableProjects.length > 1;
  const shouldShowSelector = isAuthenticated && hasMultipleProjects;

  useEffect(() => {
    if (projectsLoading || availableProjects.length === 0) return;
    const projectIdMatch = location.pathname.match(/\/projects\/([a-f0-9-]+)/);
    const urlProjectId = projectIdMatch ? projectIdMatch[1] : null;

    if (urlProjectId) {
      const urlProject = availableProjects.find(p => p.id === urlProjectId);
      if (urlProject) {
        if (!currentProject || currentProject.id !== urlProjectId) {
          setCurrentProjectState(urlProject);
          localStorage.setItem(STORAGE_KEY, urlProject.id);
        }
        return;
      }
    }

    if (currentProject && availableProjects.some(p => p.id === currentProject.id)) {
      return;
    }

    const savedProjectId = localStorage.getItem(STORAGE_KEY);

    if (savedProjectId) {
      const savedProject = availableProjects.find(p => p.id === savedProjectId);
      if (savedProject) {
        setCurrentProjectState(savedProject);
        return;
      }
    }

    // Default to first available project
    if (availableProjects.length > 0) {
      setCurrentProjectState(availableProjects[0]);
      localStorage.setItem(STORAGE_KEY, availableProjects[0].id);
    }
  }, [availableProjects, projectsLoading, currentProject, location.pathname]);

  const setCurrentProject = (project: Project | null) => {
    setCurrentProjectState(project);

    if (project) {
      localStorage.setItem(STORAGE_KEY, project.id);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }

    queryClient.invalidateQueries({ queryKey: ['stages'] });
    queryClient.invalidateQueries({ queryKey: ['blocks'] });
    queryClient.invalidateQueries({ queryKey: ['units'] });
    queryClient.invalidateQueries({ queryKey: ['typologies'] });
    queryClient.invalidateQueries({ queryKey: ['leads'] });
    queryClient.invalidateQueries({ queryKey: ['quotes'] });
    queryClient.invalidateQueries({ queryKey: ['orbit-views'] });
    queryClient.invalidateQueries({ queryKey: ['clients'] });
    queryClient.invalidateQueries({ queryKey: ['analytics'] });

    // CRITICAL: Invalidate and refetch permissions when project changes
    // The sidebar uses project-scoped permissions for access control
    queryClient.invalidateQueries({ queryKey: permissionsKeys.me() });
    if (project?.id) {
      queryClient.invalidateQueries({ queryKey: permissionsKeys.meWithProject(project.id) });
    }
    // Force immediate refetch to update sidebar
    queryClient.refetchQueries({ queryKey: permissionsKeys.me() });

    const currentPath = location.pathname;
    const isProjectSpecificRoute =
      currentPath.includes('/projects/') ||
      currentPath.includes('/stages/') ||
      currentPath.includes('/blocks/') ||
      currentPath.includes('/units/') ||
      currentPath.includes('/orbit-view/');

    if (isProjectSpecificRoute && project) {
      navigate('/');
    }

    window.dispatchEvent(new CustomEvent('projectChanged', {
      detail: { project }
    }));
  };

  const value = {
    currentProject,
    availableProjects,
    isLoading: projectsLoading,
    setCurrentProject,
    hasMultipleProjects,
    shouldShowSelector,
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
}

/**
 * Hook to access project context
 * @deprecated Use useTenant() from TenantContext instead
 * @throws Error if used outside ProjectProvider
 */
export function useProject() {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProject must be used within ProjectProvider');
  }
  return context;
}

/**
 * Safe hook to access project context
 * @deprecated Use useTenant() from TenantContext instead
 * Returns null for currentProject if used outside ProjectProvider
 * Use this in hooks/components that may render before ProjectProvider
 */
export function useProjectSafe(): Pick<ProjectContextType, 'currentProject'> {
  const context = useContext(ProjectContext);
  return {
    currentProject: context?.currentProject ?? null,
  };
}
